// src/components/sales/pages/salesNotes/SalesNotesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SalesNotesView from "./SalesNotesView";
import { salesNotesService } from "../../../../services/salesNotes.service";
import { clientsService } from "../../../../services/clients.service";

export default function SalesNotesPage({ authData, logout }) {
  const { t, i18n } = useTranslation();
  void i18n;

  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");

  const unwrap = (res) => {
    if (res && typeof res === "object" && "data" in res) return res.data;
    return res;
  };

  const loadClients = async () => {
    const res = await clientsService.list({});
    const list = unwrap(res);
    setClients(Array.isArray(list) ? list : []);
  };

  const loadNotes = async (clientId) => {
    const res = await salesNotesService.list({ clientId });
    const list = unwrap(res);
    setNotes(Array.isArray(list) ? list : []);
  };

  const refreshAll = async () => {
    setLoading(true);
    try {
      await loadClients();
      await loadNotes(selectedClientId);
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return;
      }
      alert(e?.message || t("sales.salesNotes.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // كل ما يغير client filter نعيد التحميل
    (async () => {
      setLoading(true);
      try {
        await loadNotes(selectedClientId);
      } catch (e) {
        if (e?.message === "UNAUTHORIZED") {
          logout?.();
          return;
        }
        alert(e?.message || t("sales.salesNotes.loadFailed"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  const createNote = async (payload) => {
    setLoading(true);
    try {
      await salesNotesService.create(payload);
      await loadNotes(selectedClientId);
      return true;
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return false;
      }
      alert(e?.message || t("sales.salesNotes.createFailed"));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateNote = async (id, payload) => {
    setLoading(true);
    try {
      await salesNotesService.update(id, payload);
      await loadNotes(selectedClientId);
      return true;
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return false;
      }
      alert(e?.message || t("sales.salesNotes.updateFailed"));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteNote = async (id) => {
    const ok = window.confirm(t("sales.salesNotes.confirmDelete"));
    if (!ok) return;

    setLoading(true);
    try {
      await salesNotesService.remove(id);
      await loadNotes(selectedClientId);
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return;
      }
      alert(e?.message || t("sales.salesNotes.deleteFailed"));
    } finally {
      setLoading(false);
    }
  };

  const clientsMap = useMemo(() => {
    const m = new Map();
    (clients || []).forEach((c) => m.set(String(c.id), c));
    return m;
  }, [clients]);

  return (
    <SalesNotesView
      authData={authData}
      logout={logout}
      loading={loading}
      notes={notes}
      clients={clients}
      clientsMap={clientsMap}
      selectedClientId={selectedClientId}
      setSelectedClientId={setSelectedClientId}
      onRefresh={refreshAll}
      onCreate={createNote}
      onUpdate={updateNote}
      onDelete={deleteNote}
      pageSize={5} // ✅ pagination size
    />
  );
}
